import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, TextField, IconButton,
    Box, Typography, Autocomplete, RadioGroup,
    FormControlLabel, Radio, Chip, Switch,
    Card, CardContent, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

import { searchTrip, getProblemTrips } from '../../../features/tripcenter/tripControlSlice';
import { assetNamesSelector } from '../../../features/assets/assetsSlice';
import { selectRouteListAll } from '../../../features/tripcenter/routeSlice';
import { getRouteList, searchRoutes } from '../../../features/tripcenter/routeApi';

const TripSearchPanel = ({ show, onHide }) => {
    const dispatch = useDispatch();
    const assetNames = useSelector(assetNamesSelector);
    const routeList = useSelector(selectRouteListAll);

    // State for route search
    const [routeSearchQuery, setRouteSearchQuery] = useState('');
    const [routeSearchResults, setRouteSearchResults] = useState([]);
    const [isRouteSearching, setIsRouteSearching] = useState(false);
    const [showDeletedRoutes, setShowDeletedRoutes] = useState(false);

    // Use search results if available, otherwise fall back to routeList
    const routeNameOptions = routeSearchQuery && routeSearchResults.length > 0
        ? routeSearchResults.map(route => ({
            id: route.routeName,
            label: route.routeName,
            routeNumber: route.routeNumber
        }))
        : (routeList ? [...new Set(routeList.map(route => route.routeName))]
            .map(name => ({ id: name, label: name })) : []);

    const isDeletedRouteName = (name) => typeof name === 'string' && name.includes('[DELETED]');
    const sortedRouteNameOptions = [...routeNameOptions].sort((a, b) =>
        (a?.label || '').localeCompare(b?.label || '', undefined, { sensitivity: 'base' })
    );
    const visibleRouteNameOptions = showDeletedRoutes
        ? sortedRouteNameOptions
        : sortedRouteNameOptions.filter(option => !isDeletedRouteName(option.label));

    const assetOptions = assetNames ? Object.keys(assetNames).map(key => ({
        id: key,
        value: key,
        label: assetNames[key]?.regn || ''
    })) : [];

    const tripStageOptions = [
        { value: 'Planned', label: 'Planned', color: '#4338CA' },
        { value: 'Start Delayed', label: 'Start Delayed', color: '#B45309' },
        { value: 'Active', label: 'Active', color: '#047857' },
        { value: 'Completed', label: 'Completed', color: '#1E40AF' },
        { value: 'Aborted', label: 'Aborted', color: '#B91C1C' },
        { value: 'Cancelled', label: 'Cancelled', color: '#B91C1C' }
    ];

    const [formData, setFormData] = useState({
        truckRegistrationNumber: '',
        deviceImei: '',
        routeNumber: '',
        routeName: '',
        tripStage: '',
        driverName: '',
        customer: '',
        goodsName: '',
        plannedStartTimeFrom: null,
        plannedStartTimeTo: null
    });

    const [errors, setErrors] = useState({});
    const selectedRouteIsDeleted = isDeletedRouteName(formData.routeName);

    useEffect(() => {
        if (show) {
            dispatch(getRouteList());
            setShowDeletedRoutes(false);
        }
    }, [dispatch, show]);

    // Debounced route search
    useEffect(() => {
        const searchTimeout = setTimeout(() => {
            if (routeSearchQuery && routeSearchQuery.length >= 2) {
                setIsRouteSearching(true);
                dispatch(searchRoutes({ searchQuery: routeSearchQuery, limit: 20, offset: 0 }))
                    .then((result) => {
                        if (result.payload && result.payload.routeList) {
                            setRouteSearchResults(result.payload.routeList);
                        }
                        setIsRouteSearching(false);
                    })
                    .catch(() => {
                        setIsRouteSearching(false);
                    });
            } else if (routeSearchQuery.length === 0) {
                setRouteSearchResults([]);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(searchTimeout);
    }, [routeSearchQuery, dispatch]);

    const handleVehicleSelect = (event, newValue) => {
        event?.preventDefault?.();
        setFormData(prev => ({
            ...prev,
            truckRegistrationNumber: newValue?.label || '',
            deviceImei: newValue?.value || ''
        }));
    };

    const handleRouteNameChange = (e, newValue) => {
        setFormData(prev => ({
            ...prev,
            routeName: newValue?.label || ''
        }));

        // Find the selected route from either search results or routeList
        const selectedRoute = routeSearchResults.find(route => route.routeName === newValue?.label) ||
            routeList.find(route => route.routeName === newValue?.label);

        setFormData(prev => ({
            ...prev,
            routeNumber: selectedRoute?.routeNumber || ''
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleProblemTrips = () => {
        dispatch(getProblemTrips());
        onHide();
    };

    const handleSubmit = () => {
        // Filter out undefined, null, and empty string values
        const searchParams = Object.fromEntries(
            Object.entries(formData)
                .filter(([_, value]) => {
                    if (value === null || value === undefined || value === '') return false;
                    if (moment.isMoment(value)) return value.isValid();
                    return true;
                })
                .map(([key, value]) => {
                    if (moment.isMoment(value)) {
                        return [key, value.format('YYYY-MM-DD')];
                    }
                    return [key, value];
                })
        );

        dispatch(searchTrip(searchParams));
        onHide();
    };

    const handleReset = () => {
        setFormData({
            truckRegistrationNumber: '',
            deviceImei: '',
            routeNumber: '',
            routeName: '',
            tripStage: '',
            driverName: '',
            customer: '',
            goodsName: '',
            plannedStartTimeFrom: null,
            plannedStartTimeTo: null
        });
        setRouteSearchQuery('');
        setRouteSearchResults([]);
        setShowDeletedRoutes(false);
        setErrors({});
    };

    return (
        <Dialog
            open={show}
            onClose={onHide}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 3,
                    px: 4
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SearchIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Trip Search
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Problem Trips Section */}
                <Box sx={{ p: 3, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                        color: 'white',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(238, 90, 36, 0.3)'
                        }
                    }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <WarningIcon sx={{ fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Trips with Problems
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        View all trips that have issues, violations, or delays
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleProblemTrips}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        border: '1px solid rgba(255, 255, 255, 0.5)',
                                    }
                                }}
                                startIcon={<SearchIcon />}
                            >
                                Load Problem Trips
                            </Button>
                        </CardContent>
                    </Card>
                </Box>

                {/* Search Form */}
                <Box sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                        {/* Vehicle Selection */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DirectionsCarIcon sx={{ color: 'primary.main' }} />
                                    Vehicle
                                </Typography>
                            </Box>
                            <Autocomplete
                                options={assetOptions || []}
                                getOptionLabel={(option) => option?.label || ''}
                                value={formData.deviceImei && assetOptions ?
                                    assetOptions.find(opt => opt?.value === formData.deviceImei) || null
                                    : null
                                }
                                onChange={handleVehicleSelect}
                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select vehicle..."
                                        variant="outlined"
                                        size="medium"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9'
                                                }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Route Selection */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <RouteIcon sx={{ color: 'primary.main' }} />
                                    Route
                                </Typography>
                                <FormControlLabel
                                    sx={{ margin: 0 }}
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            checked={showDeletedRoutes}
                                            onChange={(event) => setShowDeletedRoutes(event.target.checked)}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(239, 68, 68, 0.12)'
                                                    }
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: 'error.main'
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                                            Include Deleted
                                        </Typography>
                                    }
                                />
                            </Box>
                            <Autocomplete
                                options={visibleRouteNameOptions}
                                getOptionLabel={(option) => option.label}
                                value={formData.routeName ? { id: formData.routeName, label: formData.routeName } : null}
                                onChange={handleRouteNameChange}
                                onInputChange={(event, newInputValue) => {
                                    setRouteSearchQuery(newInputValue);
                                }}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                loading={isRouteSearching}
                                filterOptions={(x) => x} // Disable client-side filtering since we're using server-side search
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={routeSearchQuery ? "Searching routes..." : "Search or select route..."}
                                        variant="outlined"
                                        size="medium"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9'
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                color: selectedRouteIsDeleted ? 'error.main' : 'inherit'
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isRouteSearching ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const isDeleted = isDeletedRouteName(option.label);

                                    return (
                                        <Box component="li" {...props}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 500, color: isDeleted ? 'error.main' : 'text.primary' }}
                                                >
                                                    {option.label}
                                                </Typography>
                                                {option.routeNumber && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: isDeleted ? 'error.main' : 'text.secondary' }}
                                                    >
                                                        Route #{option.routeNumber}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                        </Grid>

                        {/* Trip Stage Radio Buttons */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                    Trip Stage
                                </Typography>
                                <RadioGroup
                                    row
                                    name="tripStage"
                                    value={formData.tripStage}
                                    onChange={handleChange}
                                    sx={{ gap: 2 }}
                                >
                                    {tripStageOptions.map((option) => (
                                        <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: option.color,
                                                        '&.Mui-checked': {
                                                            color: option.color,
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Chip
                                                    label={option.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: formData.tripStage === option.value ? option.color : '#f1f5f9',
                                                        color: formData.tripStage === option.value ? 'white' : '#64748b',
                                                        fontWeight: 500,
                                                        '&:hover': {
                                                            backgroundColor: formData.tripStage === option.value ? option.color : '#e2e8f0'
                                                        }
                                                    }}
                                                />
                                            }
                                            sx={{ margin: 0 }}
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>
                        </Grid>

                        {/* Date Range */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                                    Date Range (Trip's Planned Start Date)
                                </Typography>
                            </Box>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="Start Date"
                                            value={formData.plannedStartTimeFrom}
                                            onChange={(value) => handleDateChange('plannedStartTimeFrom', value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="medium"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="End Date"
                                            value={formData.plannedStartTimeTo}
                                            onChange={(value) => handleDateChange('plannedStartTimeTo', value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="medium"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                        </Grid>

                        {/* Text Fields */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon sx={{ color: 'primary.main' }} />
                                    Driver Name
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                name="driverName"
                                value={formData.driverName}
                                onChange={handleChange}
                                placeholder="Enter driver name..."
                                variant="outlined"
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon sx={{ color: 'primary.main' }} />
                                    Customer
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                name="customer"
                                value={formData.customer}
                                onChange={handleChange}
                                placeholder="Enter customer name..."
                                variant="outlined"
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InventoryIcon sx={{ color: 'primary.main' }} />
                                    Goods Name
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                name="goodsName"
                                value={formData.goodsName}
                                onChange={handleChange}
                                placeholder="Enter goods name..."
                                variant="outlined"
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f8fafc', gap: 2 }}>
                <Button
                    onClick={handleReset}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        borderColor: '#cbd5e1',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#94a3b8',
                            backgroundColor: '#f1f5f9'
                        }
                    }}
                >
                    Reset
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                        }
                    }}
                    startIcon={<SearchIcon />}
                >
                    Search Trips
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TripSearchPanel;
